import { CurrencyInfo } from '@percent/admin-dashboard/api/actions/currencies/currencies.types'

export const createDonationFields = (currencyInfo: CurrencyInfo[]) =>
  [
    {
      label: 'Amount (Major)',
      key: 'majorAmount',
      alternateMatches: ['amount', 'amountMajor', 'majorAmount'],
      fieldType: {
        type: 'input'
      },
      example: '12.45',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Amount is required',
          level: 'error'
        },
        {
          rule: 'regex',
          value: '^[0-9 ,.]+$',
          errorMessage: 'Amount must be a number',
          level: 'error'
        }
      ]
    },
    {
      label: 'Currency',
      key: 'currencyCode',
      alternateMatches: ['currency', 'currencyCode'],
      fieldType: {
        type: 'select',
        options: currencyInfo.map(ci => ({ value: ci.code, label: ci.code }))
      },
      example: 'GBP',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Currency is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Organisation',
      key: 'organisationId',
      alternateMatches: ['organisation'],
      fieldType: {
        type: 'input'
      },
      example: 'organisation_xxxxxxxxxxxx',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Organisation is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Partner',
      key: 'partnerId',
      alternateMatches: ['partner'],
      fieldType: {
        type: 'input'
      },
      example: 'partner_xxxxxxxxxxx',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Partner is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Subsidiary',
      key: 'subsidiaryId',
      alternateMatches: ['subsidiary', 'regional_entity'],
      fieldType: { type: 'input' },
      example: 'subsidiary_xxxxxxxxxx'
    },
    {
      label: 'Created',
      key: 'createdAt',
      alternateMatches: ['donation created', 'Created', 'created', 'created_at'],
      fieldType: {
        type: 'input'
      },
      example: '2023-06-01',
      validations: [
        {
          rule: 'regex',
          value: '^\\d{4}-\\d{2}-\\d{2}',
          errorMessage: 'Date must be in YYYY-MM-DD format',
          level: 'error'
        }
      ]
    },
    {
      label: 'Category',
      key: 'category',
      alternateMatches: ['category', 'donationCategory', 'donation_category', 'Category', 'Donation Category'],
      fieldType: { type: 'input' },
      example: 'corporate_grants',
      validations: [
        {
          rule: 'regex',
          value: '^$|^consumer_giving$|^employee_impact$|^grant|^corporate_donation$',
          errorMessage: `Invalid value. Expected corporate_donation, grant, employee_impact or consumer_giving.`,
          level: 'error'
        }
      ]
    }
  ] as const

export const createReassignmentFields = () =>
  [
    {
      label: 'Added At',
      key: 'addedAt',
      alternateMatches: ['Added At', 'added at', 'addedAt'],
      fieldType: {
        type: 'input'
      },
      example: '25/09/1995'
    },
    {
      label: 'Donation ID',
      key: 'donationId',
      alternateMatches: ['Donation ID', 'donation id', 'donationId', 'id'],
      fieldType: {
        type: 'input'
      },
      example: 'donation_xxxxxxxxxxxx',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Donation ID is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Reassignment Reason',
      key: 'reason',
      alternateMatches: ['Reassignment Reason', 'reason', 'reassignment reason'],
      fieldType: {
        type: 'input'
      },
      example: 'Incorrect organisation assignment',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Reassignment reason is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Old Organisation ID',
      key: 'oldOrganisationId',
      alternateMatches: ['Old Organisation ID', 'old organisation id', 'oldOrganisationId', 'old organisation'],
      fieldType: {
        type: 'input'
      },
      example: 'organisation_xxxxxxxxxxxx',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Old Organisation ID is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Old Organisation Name',
      key: 'oldOrganisationName',
      alternateMatches: ['Old Organisation Name', 'old organisation name', 'oldOrganisationName'],
      fieldType: {
        type: 'input'
      },
      example: 'Old Charity Name'
    },
    {
      label: 'New Organisation ID',
      key: 'newOrganisationId',
      alternateMatches: ['New Organisation ID', 'new organisation id', 'newOrganisationId', 'new organisation'],
      fieldType: {
        type: 'input'
      },
      example: 'organisation_xxxxxxxxxxxx',
      validations: [
        {
          rule: 'required',
          errorMessage: 'New Organisation ID is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'New Organisation Name',
      key: 'newOrganisationName',
      alternateMatches: ['New Organisation Name', 'new organisation name', 'newOrganisationName'],
      fieldType: {
        type: 'input'
      },
      example: 'New Charity Name'
    }
  ] as const

export const createDisbursementConfigFields = () =>
  [
    {
      label: 'Config - Type of disbursement',
      key: 'disbursementConfigurationType',
      alternateMatches: ['disbursement configuration type'],
      fieldType: {
        type: 'input'
      },
      example: 'standalone',
      validations: [
        {
          rule: 'regex',
          value: '^$|^standalone$|^bundled$',
          errorMessage: 'Disbursement configuration type must be standalone or bundled',
          level: 'error'
        }
      ]
    },
    {
      label: 'Config - Fund source',
      key: 'disbursementConfigurationFundSource',
      alternateMatches: ['disbursement configuration fund source'],
      fieldType: {
        type: 'input'
      },
      example: 'example fund source'
    },
    {
      label: 'Config - Send email',
      key: 'disbursementConfigurationSendEmail',
      alternateMatches: ['disbursement configuration send email'],
      fieldType: { type: 'input' },
      example: 'true',
      validations: [
        {
          rule: 'regex',
          value: '^$|^true$|^false$',
          errorMessage: 'Disbursement configuration send email must be true, false or undefined',
          level: 'error'
        }
      ]
    },
    {
      label: 'Config - Reference',
      key: 'disbursementConfigurationReference',
      alternateMatches: ['disbursement configuration reference'],
      fieldType: { type: 'input' },
      example: 'example reference'
    }
  ] as const

export const createPayrollDeductionFields = (currencyInfo: CurrencyInfo[]) =>
  [
    {
      label: 'Donation ID',
      key: 'donationId',
      alternateMatches: ['Donation ID', 'donation id', 'donationId', 'id'],
      fieldType: {
        type: 'input'
      },
      example: 'staging_donation_000000DDeSUOyQqWUDOrFQWGwWqhg',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Donation ID is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Currency',
      key: 'currencyCode',
      alternateMatches: ['currency', 'currencyCode'],
      fieldType: {
        type: 'select',
        options: currencyInfo.map(ci => ({ value: ci.code, label: ci.code }))
      },
      example: 'GBP',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Currency is required',
          level: 'error'
        }
      ]
    },
    {
      label: 'Payroll Deduction Amount',
      key: 'payrollDeductionAmount',
      alternateMatches: ['payroll deduction amount', 'Payroll Deduction Amount'],
      fieldType: {
        type: 'input'
      },
      example: '2222',
      validations: [
        {
          rule: 'required',
          errorMessage: 'Payroll Deduction Amount is required',
          level: 'error'
        },
        {
          rule: 'regex',
          value: '^[0-9]+$',
          errorMessage: 'Payroll Deduction Amount must be a number',
          level: 'error'
        }
      ]
    }
  ] as const
